import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

// 1. Sovereign Security & Global State
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";

// 2. Specialized Clinical Portals & Management
import HealthcareLanding from "./pages/HealthcareLanding";
import AuthPage from "./pages/AuthPage";
import AdminAuthPage from "./pages/AdminAuthPage";
import VerifyEmail from "./pages/VerifyEmail";
import AuthAction from "./pages/AuthAction";
import Documentation from "./pages/Documentation";
import ContactUs from "./pages/ContactUs";
import HelpCenter from "./pages/HelpCenter";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Updates from "./pages/Updates";
import TermsAndConditions from "./pages/TermsAndConditions";
import Status from "./pages/Status";
import ComingSoon from "./pages/ComingSoon";
import NotFound from "./pages/NotFound";
import ServerError from "./pages/ServerError";
import AIPage from "./pages/AIPage";
import About from "./pages/About";
import Specs from "./pages/Specs";
import Features from "./pages/Features";
import CookiePolicy from "./pages/CookiePolicy";
import Partnerships from "./pages/Partnerships";
import TeamPage from "./pages/TeamPage";
import ProjectReport from "./pages/ProjectReport";

// 3. Clinical Dashboard Hubs
import PatientDashboard from "./dashboard/patient/PatientDashboard";
import VitalsHistory from "./dashboard/patient/VitalsHistory";
import Appointments from "./dashboard/patient/Appointments";
import MedicalRecords from "./dashboard/patient/MedicalRecords";
import PatientSettings from "./dashboard/patient/PatientSettings";
import DoctorDashboard from "./dashboard/doctor/DoctorDashboard";
import Patients from "./dashboard/doctor/Patients";
import DoctorAppointments from "./dashboard/doctor/DoctorAppointments";
import DoctorSettings from "./dashboard/doctor/DoctorSettings";
import AdminDashboard from "./dashboard/admin/AdminDashboard";
import ManageDoctors from "./dashboard/admin/ManageDoctors";
import ManagePatients from "./dashboard/admin/ManagePatients";
import ManageHospitals from "./dashboard/admin/ManageHospitals";
import AdminSettings from "./dashboard/admin/AdminSettings";
import CaregiverDashboard from "./dashboard/caregiver/CaregiverDashboard";

// Protected Route Component with Mandatory Verification
function ProtectedRoute({ children, role }) {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return (
      <div className="global-loader">
        <div className="loader-content">
          <div className="loader-spinner"></div>
          <p>Verifying Clinical Session...</p>
        </div>
      </div>
    );
  }

  // SECURED: Gate-out unauthenticated intruders
  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  // GATED: Enforce Mandatory Identity Verification (Exempting Caregivers)
  if (!currentUser.emailVerified && userData?.role !== "caregiver") {
    return <Navigate to="/verify-email" replace />;
  }

  // IDENTITY RESOLVED: Ensure user is in the correct Sovereign Domain
  if (role && userData && userData.role && userData.role !== role) {
    // Resolve mismatch by routing to Arrival Gate
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const { currentUser, userData, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // SECURITY ENGINE: The 'Arrival Gate' Redirect Logic
    if (!loading && currentUser && userData && userData.role) {
      const currentPath = location.pathname;
      const isArrivalPath = currentPath === "/" || currentPath === "/auth" || currentPath === "/admin/auth" || currentPath === "/verify-email";

      if (isArrivalPath) {
        // Enforce Identity Verification Gate globally (Exempting Caregivers)
        if (!currentUser.emailVerified && userData?.role !== "caregiver") {
          if (currentPath !== "/verify-email") navigate("/verify-email", { replace: true });
        } else {
          // Sovereign Domain Mapping
          const dashboardMap = {
            doctor: "/dashboard/doctor",
            admin: "/dashboard/admin",
            patient: "/dashboard/patient",
            caregiver: "/dashboard/caregiver"
          };
          const target = dashboardMap[userData.role];

          if (target && currentPath !== target) {
            navigate(target, { replace: true });
          }
        }
      }
    }
  }, [currentUser, userData, loading, navigate, location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<HealthcareLanding />} />
      <Route path="/about" element={<About />} />
      <Route path="/ai" element={<AIPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/admin/auth" element={<AdminAuthPage />} />
      <Route path="/documentation" element={<Documentation />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/updates" element={<Updates />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/status" element={<Status />} />
      <Route path="/specs" element={<Specs />} />
      <Route path="/features" element={<Features />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/partnerships" element={<Partnerships />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/project-report" element={<ProjectReport />} />
      <Route path="/coming-soon" element={<ComingSoon />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/action" element={<AuthAction />} />

      {/* Patient HUD */}
      <Route path="/dashboard/patient" element={<ProtectedRoute role="patient"><PatientDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/patient/vitals-history" element={<ProtectedRoute role="patient"><VitalsHistory /></ProtectedRoute>} />
      <Route path="/dashboard/patient/appointments" element={<ProtectedRoute role="patient"><Appointments /></ProtectedRoute>} />
      <Route path="/dashboard/patient/records" element={<ProtectedRoute role="patient"><MedicalRecords /></ProtectedRoute>} />
      <Route path="/dashboard/patient/settings" element={<ProtectedRoute role="patient"><PatientSettings /></ProtectedRoute>} />

      {/* Doctor Hub */}
      <Route path="/dashboard/doctor" element={<ProtectedRoute role="doctor"><DoctorDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/doctor/patients" element={<ProtectedRoute role="doctor"><Patients /></ProtectedRoute>} />
      <Route path="/dashboard/doctor/appointments" element={<ProtectedRoute role="doctor"><DoctorAppointments /></ProtectedRoute>} />
      <Route path="/dashboard/doctor/settings" element={<ProtectedRoute role="doctor"><DoctorSettings /></ProtectedRoute>} />

      {/* Sovereign Admin Oversight */}
      <Route path="/dashboard/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/admin/doctors" element={<ProtectedRoute role="admin"><ManageDoctors /></ProtectedRoute>} />
      <Route path="/dashboard/admin/patients" element={<ProtectedRoute role="admin"><ManagePatients /></ProtectedRoute>} />
      <Route path="/dashboard/admin/hospitals" element={<ProtectedRoute role="admin"><ManageHospitals /></ProtectedRoute>} />
      <Route path="/dashboard/admin/settings" element={<ProtectedRoute role="admin"><AdminSettings /></ProtectedRoute>} />

      {/* Caregiver Oversight HUD */}
      <Route path="/dashboard/caregiver" element={<ProtectedRoute role="caregiver"><CaregiverDashboard /></ProtectedRoute>} />

      <Route path="/500" element={<ServerError />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
